import type {
  Block,
  CollectionSummary,
  NewWriting,
  Profile,
  PublishedPayload,
  TakenCard,
  PublishOptions,
  Thought,
  ThoughtQuery,
  Viewer,
  Writing,
  WritingPatch,
  WritingVersion,
} from '../types'
import { blockText, calendarDaysAgo, normalizeSpace, startOfDay } from '../text'
import { assertUploadableImage, blobToDataUrl } from '../images'
import { assertUploadableAudio } from '../audio'
import { isThoughtLike, newBlockId, quoteAttribution } from '../blocks'
import { takenSourceLine } from '../takes'
import type { WritingsBackend } from './types'
import { demoCollections, demoThoughts, demoWritings, DEMO_PHOTO_PATH, DEMO_PHOTO_URL } from './demoData'

// Local demo backend: everything lives in one localStorage document. Used when
// env.ts won't allow Supabase (or it was switched off), and by the tests.
// v2: Composer v4 demo content, narration, takes and history.

const STORAGE_KEY = 'burrow-writings-local-v2'
/** Recordings are kept as data URLs here, so keep them small. */
const MAX_LOCAL_AUDIO_BYTES = 3 * 1024 * 1024

export const LOCAL_VIEWER: Viewer = { id: 'local-niki', email: 'niki@example.com' }
export const LOCAL_PROFILE: Profile = { id: LOCAL_VIEWER.id, handle: 'niki', display_name: 'Niki', avatar_url: null }

interface LocalTake {
  writingId: string
  blockId: string
  excerpt: string
  /** The thought the take became (absent in documents from before cards). */
  thoughtId?: string
  at?: string
}

interface LocalState {
  writings: Writing[]
  thoughts: Thought[]
  collections: CollectionSummary[]
  /** `${writingId}:${blockId}` → readers who saved that quote. */
  saves: Record<string, number>
  mySaves: string[]
  /** `${writingId}:${blockId}` → the thought a saved quote became. */
  savedThoughts: Record<string, { thoughtId: string; at: string }>
  takes: LocalTake[]
  versions: WritingVersion[]
  /** thought id → collection ids it was filed into. */
  filed: Record<string, string[]>
}

export interface KeyValueStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

function browserStore(): KeyValueStore {
  const memory = new Map<string, string>()
  const fallback: KeyValueStore = {
    getItem: (k) => memory.get(k) ?? null,
    setItem: (k, v) => void memory.set(k, v),
  }
  try {
    const ls = window.localStorage
    ls.getItem(STORAGE_KEY)
    return ls
  } catch {
    return fallback
  }
}

function seed(now: Date): LocalState {
  return {
    writings: demoWritings(now, LOCAL_VIEWER.id),
    thoughts: demoThoughts(now),
    collections: demoCollections(),
    saves: {},
    mySaves: [],
    takes: [],
    versions: [],
    filed: {},
    savedThoughts: {},
  }
}

/** Older stored documents predate some fields. */
function upgrade(s: LocalState): LocalState {
  s.takes ??= []
  s.versions ??= []
  s.filed ??= {}
  s.savedThoughts ??= {}
  for (const w of s.writings) {
    w.narration ??= null
    w.cover_frame ??= null
  }
  for (const v of s.versions) v.cover_frame ??= null
  return s
}

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T

export function createLocalBackend(opts: { store?: KeyValueStore; now?: () => Date } = {}): WritingsBackend {
  const store = opts.store ?? browserStore()
  const now = opts.now ?? (() => new Date())

  const load = (): LocalState => {
    try {
      const raw = store.getItem(STORAGE_KEY)
      if (raw) return upgrade(JSON.parse(raw) as LocalState)
    } catch {
      /* corrupt or unavailable — reseed */
    }
    const fresh = seed(now())
    save(fresh)
    return fresh
  }
  const save = (s: LocalState) => {
    try {
      store.setItem(STORAGE_KEY, JSON.stringify(s))
    } catch {
      throw new Error('Local storage is full — remove a cover image or two.')
    }
  }
  const mutate = <T,>(fn: (s: LocalState) => T): T => {
    const s = load()
    const out = fn(s)
    save(s)
    return out
  }
  const findWriting = (s: LocalState, id: string): Writing => {
    const w = s.writings.find((x) => x.id === id)
    if (!w) throw new Error('That writing no longer exists.')
    return w
  }
  let seq = 0
  const newThoughtId = () => `saved-${now().getTime().toString(36)}-${++seq}`
  const file = (s: LocalState, thoughtId: string, collectionId?: string | null) => {
    const c = collectionId ? s.collections.find((x) => x.id === collectionId) : undefined
    if (!c) return
    const list = (s.filed[thoughtId] ??= [])
    if (list.includes(c.id)) return
    list.push(c.id)
    c.thought_count++
  }
  const snapshot = (s: LocalState, writingId: string, reason: WritingVersion['reason']) => {
    const w = findWriting(s, writingId)
    s.versions.push({
      id: `v-${now().getTime().toString(36)}-${++seq}`,
      writing_id: w.id,
      reason,
      title: w.title,
      subtitle: w.subtitle,
      cover_url: w.cover_url,
      cover_frame: clone(w.cover_frame ?? null),
      blocks: clone(w.blocks),
      narration: clone(w.narration ?? null),
      word_count: w.word_count,
      created_at: now().toISOString(),
    })
    // Keep the latest 100 per writing, like snapshot_writing().
    const mine = s.versions.filter((v) => v.writing_id === writingId).sort((a, b) => b.created_at.localeCompare(a.created_at))
    const drop = new Set(mine.slice(100).map((v) => v.id))
    if (drop.size) s.versions = s.versions.filter((v) => !drop.has(v.id))
  }

  const backend: WritingsBackend = {
    mode: 'local',

    async getViewer() {
      return LOCAL_VIEWER
    },
    onViewerChange() {
      return () => {}
    },
    async signInWithPassword() {},
    async sendEmailCode() {},
    async verifyEmailCode() {},
    async signOut() {},
    async getMyProfile() {
      return LOCAL_PROFILE
    },

    async listMyWritings() {
      return clone(load().writings).sort((a, b) => b.updated_at.localeCompare(a.updated_at))
    },

    async getWriting(id) {
      return clone(load().writings.find((w) => w.id === id) ?? null)
    },

    async createWriting(init: NewWriting) {
      const stamp = now().toISOString()
      const w: Writing = {
        id: globalThis.crypto?.randomUUID?.() ?? `w-${Date.now().toString(36)}`,
        owner_id: LOCAL_VIEWER.id,
        kind: init.kind ?? 'essay',
        title: init.title ?? '',
        subtitle: init.subtitle ?? '',
        cover_url: null,
        cover_frame: null,
        blocks: init.blocks ?? [{ id: newBlockId(), type: 'text', html: '' }],
        show_times: true,
        show_sources: true,
        status: 'draft',
        visibility: 'public',
        slug: null,
        published_at: null,
        collection_id: null,
        share_collection: false,
        reflection_date: init.reflection_date ?? null,
        word_count: init.word_count ?? 0,
        narration: null,
        created_at: stamp,
        updated_at: stamp,
      }
      mutate((s) => s.writings.push(w))
      return clone(w)
    },

    async updateWriting(id, patch: WritingPatch) {
      mutate((s) => Object.assign(findWriting(s, id), patch, { updated_at: now().toISOString() }))
    },

    async deleteWriting(id) {
      mutate((s) => {
        s.writings = s.writings.filter((w) => w.id !== id)
        s.versions = s.versions.filter((v) => v.writing_id !== id)
      })
    },

    async isSlugAvailable(slug, exceptId) {
      return !load().writings.some((w) => w.slug === slug && w.id !== exceptId)
    },

    async publishWriting(id, opts: PublishOptions) {
      return mutate((s) => {
        if (s.writings.some((w) => w.slug === opts.slug && w.id !== id)) {
          throw new Error('You already have a writing at that link. Pick another.')
        }
        const w = findWriting(s, id)
        const stamp = now().toISOString()
        Object.assign(w, opts, { status: 'published', published_at: w.published_at ?? stamp, updated_at: stamp })
        snapshot(s, id, 'publish')
        return clone(w)
      })
    },

    async unpublishWriting(id) {
      mutate((s) => Object.assign(findWriting(s, id), { status: 'draft', updated_at: now().toISOString() }))
    },

    async listThoughts({ search, day, limit }: ThoughtQuery) {
      const term = search?.trim().toLowerCase()
      return clone(load().thoughts)
        .filter((t) => day === undefined || calendarDaysAgo(t.created_at, now()) === day)
        .filter(
          (t) =>
            !term ||
            [t.body, t.title, t.source_title].some((f) => (f ?? '').toLowerCase().includes(term)),
        )
        .sort((a, b) => b.created_at.localeCompare(a.created_at))
        .slice(0, limit ?? 60)
    },

    async countThoughts() {
      // The design's demo account has a long history; the seeded few stand in.
      return 1204 + load().thoughts.length
    },

    async thoughtCountsByDay(days) {
      const counts = Array.from({ length: days }, () => 0)
      const since = startOfDay(days - 1, now()).getTime()
      for (const t of load().thoughts) {
        if (new Date(t.created_at).getTime() < since) continue
        const n = calendarDaysAgo(t.created_at, now())
        if (n >= 0 && n < days) counts[n]++
      }
      return counts
    },

    async thoughtImageUrl(path) {
      return path === DEMO_PHOTO_PATH ? DEMO_PHOTO_URL : null
    },

    async importThoughtImage(path) {
      return path === DEMO_PHOTO_PATH ? DEMO_PHOTO_URL : null
    },

    async listCollections() {
      // Demo state saved before collections had slugs picks theirs up from the seed.
      const seed = new Map(demoCollections().map((c) => [c.id, c]))
      return clone(load().collections).map((c) => (c.public_slug === undefined && seed.get(c.id)?.public_slug ? { ...c, public_slug: seed.get(c.id)!.public_slug } : c))
    },

    async uploadImage(file) {
      assertUploadableImage(file)
      return blobToDataUrl(file)
    },

    async uploadAudio(file) {
      assertUploadableAudio(file)
      if (file.size > MAX_LOCAL_AUDIO_BYTES) {
        throw new Error('Demo mode keeps recordings in this browser, so they must be under 3 MB (about 3 minutes).')
      }
      return blobToDataUrl(file)
    },

    async listVersions(writingId) {
      return clone(load().versions)
        .filter((v) => v.writing_id === writingId)
        .sort((a, b) => b.created_at.localeCompare(a.created_at))
    },

    async snapshotWriting(writingId, reason) {
      mutate((s) => snapshot(s, writingId, reason))
    },

    async restoreVersion(writingId, versionId) {
      return mutate((s) => {
        const v = s.versions.find((x) => x.id === versionId && x.writing_id === writingId)
        if (!v) throw new Error('That version no longer exists.')
        snapshot(s, writingId, 'restore')
        const w = findWriting(s, writingId)
        Object.assign(w, {
          title: v.title,
          subtitle: v.subtitle,
          cover_url: v.cover_url,
          cover_frame: clone(v.cover_frame ?? null),
          blocks: clone(v.blocks),
          narration: clone(v.narration),
          word_count: v.word_count,
          updated_at: now().toISOString(),
        })
        return clone(w)
      })
    },

    async getPublished(handle, slug) {
      if (handle.toLowerCase() !== LOCAL_PROFILE.handle) return null
      const s = load()
      const w = s.writings.find((x) => x.slug === slug.toLowerCase() && x.status === 'published')
      if (!w) return null
      return toPublishedPayload(w, s)
    },

    async saveQuote(writingId, blockId, _pageUrl, collectionId) {
      return mutate((s) => {
        const w = findWriting(s, writingId)
        const block = w.blocks.find((b) => b.id === blockId)
        if (!block || !(isThoughtLike(block) || block.type === 'pullquote')) throw new Error('Quote not found.')
        const key = `${writingId}:${blockId}`
        const existing = s.savedThoughts[key]
        if (s.mySaves.includes(key) && existing) {
          file(s, existing.thoughtId, collectionId)
          return existing.thoughtId
        }
        if (!s.mySaves.includes(key)) {
          s.mySaves.push(key)
          s.saves[key] = (s.saves[key] ?? 0) + 1
        }
        const thoughtLike = isThoughtLike(block) ? block : null
        const said = quoteAttribution(block)
        const id = newThoughtId()
        s.savedThoughts[key] = { thoughtId: id, at: now().toISOString() }
        s.thoughts.push({
          id,
          title: null,
          body: blockText(block),
          source_url: thoughtLike?.source?.url ?? null,
          // As save_writing_quote: an attributed pull quote credits whoever said it, via the writing.
          source_title: thoughtLike?.source?.title ? thoughtLike.source.title : said ? takenSourceLine(w.title, LOCAL_PROFILE.display_name ?? '', said) : w.title,
          image_path: null,
          created_at: now().toISOString(),
        })
        file(s, id, collectionId)
        return id
      })
    },

    async saveLine(writingId, blockId, text, _pageUrl, collectionId) {
      return mutate((s) => {
        const w = findWriting(s, writingId)
        const block = w.blocks.find((b) => b.id === blockId)
        const excerpt = normalizeSpace(text)
        if (!excerpt) throw new Error('Pick a line to take.')
        if (!block || !normalizeSpace(blockText(block)).includes(excerpt)) throw new Error('Line not found.')
        const taken = s.takes.find((t) => t.writingId === writingId && t.blockId === blockId && t.excerpt === excerpt)
        if (taken?.thoughtId) {
          file(s, taken.thoughtId, collectionId)
          return taken.thoughtId
        }
        const id = newThoughtId()
        if (taken) Object.assign(taken, { thoughtId: id, at: now().toISOString() })
        else s.takes.push({ writingId, blockId, excerpt, thoughtId: id, at: now().toISOString() })
        s.thoughts.push({
          id,
          title: null,
          body: excerpt,
          source_url: null,
          source_title: takenSourceLine(w.title, LOCAL_PROFILE.display_name ?? '', quoteAttribution(block)),
          image_path: null,
          created_at: now().toISOString(),
        })
        file(s, id, collectionId)
        return id
      })
    },

    async refileTakenThought(thoughtId, collectionId) {
      mutate((s) => {
        const isTake =
          s.takes.some((t) => t.thoughtId === thoughtId) || Object.values(s.savedThoughts).some((v) => v.thoughtId === thoughtId)
        if (!isTake || !s.thoughts.some((t) => t.id === thoughtId)) throw new Error('Card not found.')
        if (collectionId && !s.collections.some((c) => c.id === collectionId)) throw new Error('Collection not found.')
        // Out of every other collection, into the chosen one (or loose), like refile_taken_thought().
        for (const cid of s.filed[thoughtId] ?? []) {
          if (cid === collectionId) continue
          const c = s.collections.find((x) => x.id === cid)
          if (c) c.thought_count = Math.max(0, c.thought_count - 1)
        }
        s.filed[thoughtId] = (s.filed[thoughtId] ?? []).filter((cid) => cid === collectionId)
        file(s, thoughtId, collectionId)
      })
    },

    async createCollection(name) {
      const clean = name.trim().slice(0, 120)
      if (!clean) throw new Error('Give the collection a name.')
      return mutate((s) => {
        const c: CollectionSummary = { id: `c-${now().getTime().toString(36)}-${++seq}`, name: clean, is_public: false, cover_color: null, thought_count: 0 }
        s.collections.unshift(c)
        return clone(c)
      })
    },

    async setFollowing() {
      /* the demo reader is always the author */
    },
  }

  /** The demo reader's taken cards on this writing, like get_published_writing()'s my_cards. */
  function myCards(w: Writing, s: LocalState): TakenCard[] {
    const text = (thoughtId: string, fallback: string) => s.thoughts.find((t) => t.id === thoughtId)?.body || fallback
    const lines: TakenCard[] = s.takes
      .filter((t) => t.writingId === w.id && t.thoughtId && s.thoughts.some((x) => x.id === t.thoughtId))
      .map((t) => ({
        kind: 'line',
        block_id: t.blockId,
        text: text(t.thoughtId!, t.excerpt),
        thought_id: t.thoughtId!,
        created_at: t.at ?? w.updated_at,
        collection_ids: [...(s.filed[t.thoughtId!] ?? [])],
      }))
    const quotes: TakenCard[] = Object.entries(s.savedThoughts)
      .filter(([key, v]) => key.startsWith(`${w.id}:`) && s.thoughts.some((x) => x.id === v.thoughtId))
      .map(([key, v]) => ({
        kind: 'quote',
        block_id: key.slice(w.id.length + 1),
        text: text(v.thoughtId, ''),
        thought_id: v.thoughtId,
        created_at: v.at,
        collection_ids: [...(s.filed[v.thoughtId] ?? [])],
      }))
    return [...lines, ...quotes].sort((a, b) => a.created_at.localeCompare(b.created_at))
  }

  function toPublishedPayload(w: Writing, s: LocalState): PublishedPayload {
    const author = {
      ...LOCAL_PROFILE,
      published_count: s.writings.filter((x) => x.status === 'published' && x.visibility === 'public').length,
    }
    // Same stripping as get_published_writing(): hidden times and sources never leave the backend.
    const blocks: Block[] = w.blocks.map((b) => {
      const out: Record<string, unknown> = { ...b }
      delete out.thoughtId
      if (!w.show_times) {
        delete out.capturedAt
        delete out.kind
      }
      if (!w.show_sources) delete out.source
      return out as unknown as Block
    })
    const saves: Record<string, number> = {}
    for (const [key, n] of Object.entries(s.saves)) {
      const [wid, bid] = key.split(':')
      if (wid === w.id) saves[bid] = n
    }
    const myTakes = s.takes.filter((t) => t.writingId === w.id)
    // One demo reader: a block counts once whether they took a line, the quote, or both.
    const takeCounts: Record<string, number> = { ...saves }
    for (const bid of new Set(myTakes.map((t) => t.blockId))) {
      if (!s.mySaves.includes(`${w.id}:${bid}`)) takeCounts[bid] = (takeCounts[bid] ?? 0) + 1
    }
    const collection = w.share_collection ? s.collections.find((c) => c.id === w.collection_id && c.is_public) : undefined
    return {
      gated: false,
      writing: {
        id: w.id,
        kind: w.kind,
        title: w.title,
        subtitle: w.subtitle,
        cover_url: w.cover_url,
        cover_frame: w.cover_frame ?? null,
        slug: w.slug ?? undefined,
        visibility: w.visibility,
        published_at: w.published_at,
        word_count: w.word_count,
        show_times: w.show_times,
        show_sources: w.show_sources,
        narration: w.narration ?? null,
        blocks,
      },
      author,
      viewer: {
        signed_in: true,
        is_owner: true,
        follows: false,
        avatar_url: null,
        collections: s.collections.map((c) => ({ id: c.id, name: c.name, cover_color: c.cover_color })),
      },
      saves,
      my_saves: s.mySaves.filter((k) => k.startsWith(`${w.id}:`)).map((k) => k.split(':')[1]),
      take_counts: takeCounts,
      my_takes: myTakes.map((t) => ({ block_id: t.blockId, excerpt: t.excerpt })),
      my_cards: myCards(w, s),
      collection: collection ? { ...collection, description: null } : null,
      more: s.writings
        .filter((x) => x.status === 'published' && x.visibility === 'public' && x.id !== w.id && x.slug)
        .sort((a, b) => (b.published_at ?? '').localeCompare(a.published_at ?? ''))
        .slice(0, 3)
        .map((x) => ({
          id: x.id,
          title: x.title,
          slug: x.slug!,
          cover_url: x.cover_url,
          cover_frame: x.cover_frame ?? null,
          published_at: x.published_at!,
          word_count: x.word_count,
        })),
    }
  }

  return backend
}
